import { type HexColor, type Sheet, type Table } from './spreadsheetModel';

// File content generated and managed by AI agent.

export type OoxmlFile = {
  name: string;
  content: string;
};

export type OoxmlPackage = {
  files: OoxmlFile[];
};

type StyleKey = {
  bold: boolean;
  alignment: 'left' | 'center' | 'right';
  fillColor?: HexColor;
};

type StyleContext = {
  styleKeys: StyleKey[];
  styleKeyToId: Map<string, number>;
};

type FontKey = {
  bold: boolean;
};

type FillKey =
  | { type: 'none' }
  | { type: 'gray125' }
  | { type: 'color'; color: HexColor };

const HEADER_BACKGROUND_COLOR: HexColor = '#D9D9D9';

const DEFAULT_STYLE_KEY: StyleKey = {
  bold: false,
  alignment: 'left',
};

const escapeXml = (value: string): string =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');

const serializeStyleKey = (style: StyleKey): string =>
  `${style.bold ? '1' : '0'}|${style.alignment}|${style.fillColor ?? ''}`;

const createStyleContext = (): StyleContext => ({
  styleKeys: [DEFAULT_STYLE_KEY],
  styleKeyToId: new Map([[serializeStyleKey(DEFAULT_STYLE_KEY), 0]]),
});

const getStyleId = (context: StyleContext, style: StyleKey): number => {
  const key = serializeStyleKey(style);
  const existing = context.styleKeyToId.get(key);

  if (existing !== undefined) {
    return existing;
  }

  const id = context.styleKeys.length;
  context.styleKeys.push(style);
  context.styleKeyToId.set(key, id);

  return id;
};

const toColumnName = (index: number): string => {
  let result = '';
  let current = index;

  while (current >= 0) {
    const remainder = current % 26;
    result = String.fromCharCode(65 + remainder) + result;
    current = Math.floor(current / 26) - 1;
  }

  return result;
};

const estimateColumnWidth = (charCount: number | undefined): number => {
  if (!charCount || charCount <= 0) {
    return 10;
  }

  const width = charCount * 1.1 + 2;

  if (width < 10) {
    return 10;
  }
  if (width > 60) {
    return 60;
  }

  return width;
};

const buildColsXml = (
  maxColumns: number,
  maxCharsPerColumn: number[],
): string => {
  if (maxColumns === 0) {
    return '';
  }

  const cols = Array.from({ length: maxColumns }, (_, index) => {
    const charCount = maxCharsPerColumn[index];
    const width = estimateColumnWidth(charCount);

    return `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`;
  }).join('');

  return `<cols>${cols}</cols>`;
};

const buildSheetViewsXml = (table: Table): string => {
  if (table.frozenRowCount <= 0) {
    return '';
  }

  const ySplit = table.frozenRowCount;
  const topLeftCellRow = ySplit + 1;
  const topLeftCell = `A${topLeftCellRow}`;

  return [
    '<sheetViews>',
    '<sheetView workbookViewId="0">',
    `<pane ySplit="${ySplit}" topLeftCell="${topLeftCell}" activePane="bottomLeft" state="frozen"/>`,
    '</sheetView>',
    '</sheetViews>',
  ].join('');
};

const buildWorksheetXml = (
  sheet: Sheet,
  sheetIndex: number,
  styleContext: StyleContext,
): OoxmlFile => {
  const { table } = sheet;

  let maxColumns = 0;
  const maxCharsPerColumn: number[] = [];

  const sheetDataRowsXml = table.rows
    .map((row, rowIndex) => {
      const rowNumber = rowIndex + 1;
      const isHeaderRow = rowIndex < table.headerRowCount;

      maxColumns = Math.max(maxColumns, row.cells.length);

      const cellsXml = row.cells
        .map((cell, columnIndex) => {
          const columnName = toColumnName(columnIndex);
          const cellReference = `${columnName}${rowNumber}`;
          const fillColor: HexColor | undefined =
            cell.backgroundColor ??
            (isHeaderRow ? HEADER_BACKGROUND_COLOR : undefined);
          const bold = cell.bold || isHeaderRow;

          const styleId = getStyleId(styleContext, {
            bold,
            alignment: cell.alignment,
            fillColor,
          });

          if (cell.content != null) {
            const length = cell.content.length;
            const currentMax = maxCharsPerColumn[columnIndex] ?? 0;
            if (length > currentMax) {
              maxCharsPerColumn[columnIndex] = length;
            }
          }

          if (cell.content == null) {
            return `<c r="${cellReference}" s="${styleId}"/>`;
          }

          const value = escapeXml(cell.content);
          return `<c r="${cellReference}" t="inlineStr" s="${styleId}"><is><t xml:space="preserve">${value}</t></is></c>`;
        })
        .join('');

      return `<row r="${rowNumber}">${cellsXml}</row>`;
    })
    .join('');

  const hasCells = table.rows.length > 0 && maxColumns > 0;
  const lastColumnName = hasCells ? toColumnName(maxColumns - 1) : 'A';
  const lastRowNumber = hasCells ? table.rows.length : 1;
  const dimensionReference = hasCells
    ? `A1:${lastColumnName}${lastRowNumber}`
    : 'A1';

  const sheetViewsXml = buildSheetViewsXml(table);
  const colsXml = buildColsXml(maxColumns, maxCharsPerColumn);

  const headerRowNumber =
    table.headerRowCount > 0
      ? Math.min(table.headerRowCount, lastRowNumber)
      : 0;

  const autoFilterXml =
    headerRowNumber > 0 && hasCells
      ? `<autoFilter ref="A${headerRowNumber}:${lastColumnName}${headerRowNumber}"/>`
      : '';

  const content = [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"',
    ' xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">',
    `<dimension ref="${dimensionReference}"/>`,
    sheetViewsXml,
    '<sheetFormatPr defaultRowHeight="15"/>',
    colsXml,
    '<sheetData>',
    sheetDataRowsXml,
    '</sheetData>',
    autoFilterXml,
    '</worksheet>',
  ].join('');

  return {
    name: `xl/worksheets/sheet${sheetIndex + 1}.xml`,
    content,
  };
};

const buildStylesXml = (context: StyleContext): string => {
  const fontKeyToId = new Map<string, number>();
  const fontKeys: FontKey[] = [];

  const fillKeyToId = new Map<string, number>();
  const fillKeys: FillKey[] = [];

  const ensureFontId = (key: FontKey): number => {
    const serialized = key.bold ? '1' : '0';
    const existing = fontKeyToId.get(serialized);

    if (existing !== undefined) {
      return existing;
    }

    const id = fontKeys.length;
    fontKeys.push(key);
    fontKeyToId.set(serialized, id);

    return id;
  };

  const ensureFillId = (key: FillKey): number => {
    let serialized: string;

    if (key.type === 'none') {
      serialized = 'none';
    } else if (key.type === 'gray125') {
      serialized = 'gray125';
    } else {
      serialized = key.color;
    }
    const existing = fillKeyToId.get(serialized);

    if (existing !== undefined) {
      return existing;
    }

    const id = fillKeys.length;
    fillKeys.push(key);
    fillKeyToId.set(serialized, id);

    return id;
  };

  ensureFontId({ bold: false });
  ensureFillId({ type: 'none' });
  ensureFillId({ type: 'gray125' });

  type Xf = {
    fontId: number;
    fillId: number;
    alignment: 'left' | 'center' | 'right';
  };

  const xfs: Xf[] = context.styleKeys.map((style) => {
    const fontId = ensureFontId({ bold: style.bold });
    const fillId = ensureFillId(
      style.fillColor
        ? { type: 'color', color: style.fillColor }
        : { type: 'none' },
    );

    return {
      fontId,
      fillId,
      alignment: style.alignment,
    };
  });

  const fontsXml = fontKeys
    .map((font) => {
      const boldTag = font.bold ? '<b/>' : '';
      return `<font>${boldTag}<name val="Calibri"/><family val="2"/><sz val="11"/></font>`;
    })
    .join('');

  const fillsXml = fillKeys
    .map((fill) => {
      if (fill.type === 'none') {
        return '<fill><patternFill patternType="none"/></fill>';
      }

      if (fill.type === 'gray125') {
        return '<fill><patternFill patternType="gray125"/></fill>';
      }

      const rgb = fill.color.slice(1);
      return [
        '<fill>',
        '<patternFill patternType="solid">',
        `<fgColor rgb="FF${rgb}"/>`,
        '<bgColor indexed="64"/>',
        '</patternFill>',
        '</fill>',
      ].join('');
    })
    .join('');

  const bordersXml =
    '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>';

  const cellStyleXfsXml =
    '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>';

  const cellXfsXml = xfs
    .map((xf) => {
      return [
        '<xf numFmtId="0"',
        ` fontId="${xf.fontId}"`,
        ` fillId="${xf.fillId}"`,
        ' borderId="0" applyFont="1" applyFill="1" applyAlignment="1">',
        `<alignment horizontal="${xf.alignment}"/>`,
        '</xf>',
      ].join('');
    })
    .join('');

  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">',
    `<fonts count="${fontKeys.length}">${fontsXml}</fonts>`,
    `<fills count="${fillKeys.length}">${fillsXml}</fills>`,
    bordersXml,
    cellStyleXfsXml,
    `<cellXfs count="${xfs.length}">${cellXfsXml}</cellXfs>`,
    '</styleSheet>',
  ].join('');
};

const buildWorkbookXml = (sheets: Sheet[]): string => {
  const sheetsXml = sheets
    .map((sheet, index) => {
      const name = escapeXml(sheet.name);
      const id = index + 1;
      const relId = `rId${id}`;
      return `<sheet name="${name}" sheetId="${id}" r:id="${relId}"/>`;
    })
    .join('');

  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"',
    ' xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">',
    `<sheets>${sheetsXml}</sheets>`,
    '</workbook>',
  ].join('');
};

const buildWorkbookRelsXml = (sheetCount: number): string => {
  const sheetRelationshipsXml = Array.from(
    { length: sheetCount },
    (_, index) => {
      const id = index + 1;
      return [
        `<Relationship Id="rId${id}"`,
        ' Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet"',
        ` Target="worksheets/sheet${id}.xml"/>`,
      ].join('');
    },
  ).join('');

  const stylesRelationshipId = sheetCount + 1;

  const stylesRelationshipXml = [
    `<Relationship Id="rId${stylesRelationshipId}"`,
    ' Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles"',
    ' Target="styles.xml"/>',
  ].join('');

  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
    sheetRelationshipsXml,
    stylesRelationshipXml,
    '</Relationships>',
  ].join('');
};

const buildRootRelsXml = (): string =>
  [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>',
    '</Relationships>',
  ].join('');

const buildContentTypesXml = (sheetCount: number): string => {
  const sheetOverridesXml = Array.from({ length: sheetCount }, (_, index) => {
    const id = index + 1;
    return [
      `<Override PartName="/xl/worksheets/sheet${id}.xml"`,
      ' ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>',
    ].join('');
  }).join('');

  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">',
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>',
    '<Default Extension="xml" ContentType="application/xml"/>',
    '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>',
    sheetOverridesXml,
    '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>',
    '</Types>',
  ].join('');
};

/**
 * Generate OOXML data files for the given sheets.
 *
 * The implementation is intentionally minimal and does not attempt to
 * validate the generated XML. It is expected that a higher-level
 * layer assembles these files into a .xlsx archive.
 */
export const generateOoxmlPackage = (sheets: Sheet[]): OoxmlPackage => {
  const styleContext = createStyleContext();

  const worksheetFiles = sheets.map((sheet, index) =>
    buildWorksheetXml(sheet, index, styleContext),
  );

  const stylesXml = buildStylesXml(styleContext);
  const workbookXml = buildWorkbookXml(sheets);
  const workbookRelsXml = buildWorkbookRelsXml(sheets.length);
  const rootRelsXml = buildRootRelsXml();
  const contentTypesXml = buildContentTypesXml(sheets.length);

  const files: OoxmlFile[] = [
    {
      name: '[Content_Types].xml',
      content: contentTypesXml,
    },
    {
      name: '_rels/.rels',
      content: rootRelsXml,
    },
    {
      name: 'xl/workbook.xml',
      content: workbookXml,
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      content: workbookRelsXml,
    },
    {
      name: 'xl/styles.xml',
      content: stylesXml,
    },
    ...worksheetFiles,
  ];

  return { files };
};
